/* eslint-disable jsx-a11y/aria-role */
import { useEffect, useReducer, useState } from 'react';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
  IconHamburgerMenu,
  IconTimesDefault,
  IconUserFilled,
} from '@iwoca/icons';
import { Button, Spinner } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { AddNewApplicationNavItem } from 'components/addNewApplication/AddNewApplicationNavItem';
import { Logo } from 'components/Logo/Logo';
import { StagingBanner } from 'components/StagingBanner/StagingBanner';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { IwQueryClient } from 'store/IwQueryClient';
import { getAmplitudeDefaultEventProperties } from 'utils/amplitudeEvents';
import { getCookie } from 'utils/cookieHelper';
import { getEnvironment } from 'utils/environmentHelper/environmentHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { BREAK_M, BREAK_XS } from '../../utils/breakpoints';
import { ContactUsButton } from './ContactUsButton/ContactUsButton';
import { DropdownContainer } from './DropdownContainer/DropdownContainer';
import { FAQsButton } from './FAQsButton/FAQsButton';
import { HelpModal } from './Help/HelpModal';
import { InviteModal } from './Invite/InviteModal';
import {
  ACTIONS,
  dropdownReducer,
  initialReducerState,
  useInviteColleague,
} from './Navigation.helpers';
import styles from './Navigation.module.css';
import { NavItemLink } from './NavItemLink/NavItemLink';
import { WhatsNewButton } from './WhatsNewButton/WhatsNewButton';

export const Navigation = () => {
  const { isDE, isUK } = useLocale();
  const { trackEvent } = useAmplitude();
  const [state, dispatch] = useReducer(dropdownReducer, initialReducerState);
  const [shouldShowInviteModal, setShouldShowInviteModal] = useState(false);

  const logoutLink = isDE ? '/partner/logout/' : '/introducer/logout';
  const [showHelpModal, setShowHelpModal] = useState(false);
  const {
    isContextLoading,
    introducer,
    introducerId,
    isIntroducerLoading,
    hasProductsForApplication,
    introducerCompany,
    hasOnboardingFilesUploaded,
    splitTests,
  } = useIntroducerData();
  const showPortalTours =
    getCookie('showPortalTours') ||
    splitTests?.introducer_portal_onboarding_tutorials;
  const { canInviteColleague } = useInviteColleague();

  const isPartner = introducerCompany?.introducer_type === 'partner';
  const firstName = introducer?.first_name;
  const lastName = introducer?.last_name;
  const companyName =
    introducerCompany?.public_facing_company_name ||
    introducerCompany?.company_name;

  const handleAccountDropdown = () => {
    dispatch({ type: ACTIONS.ACCOUNT });
  };

  const handleMenuDropdown = () => {
    dispatch({ type: ACTIONS.MOBILE_MENU });
  };

  const handleCloseDropdown = () => {
    dispatch({ type: ACTIONS.CLOSE });
  };

  const { width } = useWindowDimensions();
  const showDesktopMenu = width >= BREAK_M;

  useEffect(() => {
    handleCloseDropdown();
  }, [showDesktopMenu]);

  const disableNavButton =
    !hasProductsForApplication || !hasOnboardingFilesUploaded;

  const toggleMobileMenu = () => {
    if (state.isDropdownOpen) {
      handleCloseDropdown();
    } else {
      trackEvent(AMPLITUDE_EVENTS.navigationOpened, {
        'CTA destination': 'Menu',
        'CTA text': 'Menu',
      });
      handleMenuDropdown();
    }
  };

  const renderNavItems = () => {
    return (
      <>
        <NavItemLink
          href={PATH_NAMES.home()}
          label={t`Dashboard`}
          onClick={() => {
            IwQueryClient.getOrCreate().invalidateQueries({
              queryKey: ['applications'],
            });
            trackEvent(AMPLITUDE_EVENTS.internalLinkClicked, {
              'CTA destination': 'Portal',
              'CTA text': 'Dashboard',
              'CTA URL': PATH_NAMES.home(),
            });
          }}
          disabled={disableNavButton}
        />
        {!isPartner && (
          <NavItemLink
            href={PATH_NAMES.products()}
            label={t`Products`}
            disabled={Boolean(introducerId && !hasOnboardingFilesUploaded)}
            onClick={() => {
              trackEvent(AMPLITUDE_EVENTS.internalLinkClicked, {
                'CTA destination': 'Products',
                'CTA text': 'Products',
                'CTA URL': PATH_NAMES.products,
              });
            }}
          />
        )}
        <NavItemLink
          href={PATH_NAMES.calculator()}
          label={t`Calculator`}
          onClick={() => {
            trackEvent(AMPLITUDE_EVENTS.internalLinkClicked, {
              'CTA destination': 'Calculator',
              'CTA text': 'Calculator',
              'CTA URL': PATH_NAMES.calculator(),
            });
          }}
          disabled={disableNavButton}
        />
      </>
    );
  };

  const renderAccountButtonContent = () => {
    if (isContextLoading || isIntroducerLoading) {
      return <Spinner />;
    }

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`;
    }

    return <IconUserFilled data-testid="account-icon-fallback" />;
  };

  const renderAccountIcon = () => {
    return (
      <Button
        role="account"
        className={styles.accountIcon}
        variant="denim"
        onClick={() => {
          if (showDesktopMenu) {
            trackEvent(AMPLITUDE_EVENTS.accountMenuClicked);
            handleAccountDropdown();
          }
        }}
        size="large"
        data-testid="account-icon-button"
        id="account-icon-button"
      >
        {renderAccountButtonContent()}
      </Button>
    );
  };

  const renderAccountDetail = () => {
    if (!firstName && !lastName) {
      return null;
    }

    return (
      <div
        className={clsx(styles.navItem, styles.navAccountDetail, {
          [styles.mobileDetails]: !showDesktopMenu,
        })}
      >
        {!showDesktopMenu && renderAccountIcon()}
        <div className={styles.detailsContainer}>
          {companyName && (
            <span className={styles.companyName}>{companyName}</span>
          )}
          <span className={styles.brokerName}>
            {firstName} {lastName}
          </span>
        </div>
      </div>
    );
  };

  const renderAccountOverviewLink = () => {
    if (!isUK || isPartner) return null;
    return (
      <NavItemLink
        data-testid="account-overview-link"
        href={PATH_NAMES.accountOverview()}
        label="Account overview"
        disabled={!introducerId}
        onClick={() => {
          tracking.ampli.ctaClick({
            ...getAmplitudeDefaultEventProperties(),
            'CTA text': 'Account overview',
          });
        }}
      />
    );
  };

  const renderUserManagementLink = () => {
    if (!isUK || isPartner || !getCookie('showUserManagement')) return null;
    return (
      <NavItemLink
        data-testid="user-management-link"
        href={PATH_NAMES.userManagement()}
        label="User management"
        disabled={!introducerId}
        onClick={() => {
          tracking.ampli.ctaClick({
            ...getAmplitudeDefaultEventProperties(),
            'CTA text': 'User management',
          });
        }}
      />
    );
  };

  const renderAccountDropdownContent = () => {
    return (
      <>
        {canInviteColleague && (
          <div className={styles.navItem}>
            <button
              className={styles.navItemLink}
              onClick={() => {
                trackEvent(AMPLITUDE_EVENTS.modalOpened, {
                  'Modal name': 'Invite modal',
                });
                setShouldShowInviteModal(true);
              }}
            >
              <Trans>Invite your colleagues</Trans>
            </button>
          </div>
        )}
        {showPortalTours && (
          <NavItemLink
            data-testid="portal-tours-link"
            href={PATH_NAMES.portalTours()}
            label={t`Portal tours`}
            disabled={!introducerId}
            onClick={() => {
              tracking.ampli.ctaClick({
                ...getAmplitudeDefaultEventProperties(),
                'CTA text': 'Portal tours',
              });
            }}
          />
        )}
        <ContactUsButton setShowHelpModal={setShowHelpModal} />
        <div className={styles.navItem}>
          <a
            href={logoutLink}
            className={clsx(styles.navItemLink, styles.logoutLink)}
            onClick={() => {
              trackEvent(AMPLITUDE_EVENTS.loggedOut, {
                'CTA destination': 'Logout',
                'CTA text': 'Logout',
                'CTA URL': logoutLink,
              });
            }}
          >
            <Trans>Logout</Trans>
          </a>
        </div>
      </>
    );
  };

  const renderMobileMenu = () => {
    return (
      <>
        {renderAccountDetail()}
        {renderNavItems()}
        {introducerId && (
          <>
            <WhatsNewButton introducerId={introducerId} />
            <FAQsButton isDisabled={disableNavButton} />
          </>
        )}
        {renderAccountOverviewLink()}
        {renderUserManagementLink()}
        {renderAccountDropdownContent()}
      </>
    );
  };

  const renderAccountDropdownSection = () => {
    return (
      <DropdownContainer
        onClose={handleCloseDropdown}
        className={clsx(styles.navDropdown, {
          [styles.accountDropdown]: state.selectedDropdown === ACTIONS.ACCOUNT,
          [styles.mobileMenuDropdown]:
            state.selectedDropdown === ACTIONS.MOBILE_MENU,
        })}
      >
        {state.selectedDropdown === ACTIONS.ACCOUNT && (
          <>
            {renderAccountDetail()}
            {renderAccountOverviewLink()}
            {renderUserManagementLink()}
            {renderAccountDropdownContent()}
          </>
        )}
        {state.selectedDropdown === ACTIONS.MOBILE_MENU && renderMobileMenu()}
      </DropdownContainer>
    );
  };

  const renderDesktopAccountItems = () => {
    return (
      <div className={clsx(styles.accountItem, styles.accountIconWrapper)}>
        {renderAccountIcon()}
      </div>
    );
  };

  const renderMobileMenuButton = () => {
    return (
      <div className={styles.accountItem}>
        <Button
          variant="tertiary"
          className={clsx(styles.navButton, styles.mobileMenuButton)}
          data-testid="menu-button"
          onClick={toggleMobileMenu}
        >
          <div
            className={clsx(styles.hamburgerIconWrapper, {
              [styles.isOpen]: state.isDropdownOpen,
            })}
          >
            {state.isDropdownOpen ? (
              <IconTimesDefault className={styles.hamburgerIcon} />
            ) : (
              <IconHamburgerMenu className={styles.hamburgerIcon} />
            )}
          </div>
        </Button>
      </div>
    );
  };

  const renderModals = () => {
    return (
      <>
        <InviteModal
          showModal={shouldShowInviteModal}
          closeModal={() => setShouldShowInviteModal(false)}
        />
        <HelpModal
          showModal={showHelpModal}
          closeModal={() => setShowHelpModal(false)}
        />
      </>
    );
  };

  return (
    <div
      className={clsx(styles.navigationWrapper, {
        [styles.isStaging]: getEnvironment() === 'staging',
      })}
    >
      <StagingBanner />
      <nav className={styles.navigation} data-testid="navigation">
        <div className={styles.navItems}>
          <Logo iconOnly={width <= BREAK_XS} />
          <div className={styles.navCTAsWrapper} id="nav-ctas-left">
            {showDesktopMenu && renderNavItems()}

            <div className={styles.navItem}>
              <AddNewApplicationNavItem
                showDesktopVersion={width >= BREAK_XS}
              />
            </div>
          </div>
        </div>

        <div className={styles.accountItems} id="nav-ctas-right">
          {showDesktopMenu && introducerId && (
            <>
              <div className={styles.accountItem}>
                <WhatsNewButton
                  disabled={disableNavButton}
                  introducerId={introducerId}
                />
              </div>
              <div className={styles.accountItem}>
                <FAQsButton isDisabled={disableNavButton} />
              </div>
            </>
          )}
          {showDesktopMenu && renderDesktopAccountItems()}
          {!showDesktopMenu && renderMobileMenuButton()}
        </div>
      </nav>

      {/* Modals and dropdowns */}
      {state.isDropdownOpen && renderAccountDropdownSection()}
      {renderModals()}
    </div>
  );
};
